<template>
	<page-container :page="page">
		<heading-image
			v-if="page && page.headingImage"
			:heading-image="{
				image: page.headingImage,
				imageWebp: page.headingImageWebp,
				imagePortrait: page.headingImagePortrait,
				imagePortraitWebp: page.headingImagePortraitWebp,
			}"
		/>

		<main-content
			:title="page.title || page.header"
			:title-right="page.titleRight"
			:subtitle="page.subtitle"
			:content="page.content"
			:intro-image="page.introImage"
			:intro-image-webp="page.introImageWebp"
			:intro-image-portrait="page.introImagePortrait"
			:intro-image-portrait-webp="page.introImagePortraitWebp"
		>
			<template #rightContent>
				<div class="columns column6 intro-images">
					<iframe
						width="100%"
						height="385"
						src="https://www.youtube.com/embed/XcGVZoDz_dM?si=vlTeOsTjw3QNTBI-"
						title="YouTube video player"
						frameborder="0"
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
						referrerpolicy="strict-origin-when-cross-origin"
						allowfullscreen
					></iframe>
				</div>
			</template>
		</main-content>

		<section class="main-content background-dark">
			<div class="row rooms align-left">
				<div v-for="room in roomsData" :key="room.ID" class="columns column6 room-item">
					<div>
						<div class="hidden-overflow">
							<nuxt-link :to="localePath(`/${room.filename}`)">
								<picture>
									<source :srcset="room.imageOverviewWebp" type="image/webp" />
									<source :srcset="room.imageOverview" />
									<img
										class="room-img"
										:src="room.imageOverview"
										:alt="room.imageOverviewAlt"
										loading="lazy"
									/>
								</picture>
							</nuxt-link>
						</div>
					</div>
					<div class="content">
						<h2>
							<nuxt-link :to="localePath(`/${room.filename}`)">
								{{ room.title || room.header }}
							</nuxt-link>
						</h2>
						<div v-parse-links v-html="room.contentShort" />
						<div class="room-icons">
							<div v-if="room.pax" class="room-icon">
								<font-awesome-icon :icon="`fal fa-users`" size="1x" />
								<span>{{ room.pax }}</span>
							</div>
							<div v-if="room.surface" class="room-icon">
								<font-awesome-icon :icon="`fal fa-ruler-combined`" size="1x" />
								<span>{{ room.surface }}</span>
							</div>
							<div v-if="room.lounge" class="room-icon">
								<font-awesome-icon :icon="`fal fa-couch`" size="1x" />
								<span>{{ room.lounge }}</span>
							</div>
						</div>
						<button-container class="rooms-button">
							<nuxt-link :to="localePath(`/${room.filename}`)" class="button-arrow">
								{{ $t('viewThisRoom') }}
							</nuxt-link>
							<book-button v-if="room.bookingID" :roomid="room.bookingID" class="button">
								{{ $t('bookThisRoom') }}
							</book-button>
						</button-container>
					</div>
				</div>
			</div>
		</section>

		<contentblocks v-if="page.contentblocks && page.contentblocks.length" :items="page.contentblocks" />

		<review-slider v-if="page.showReviewSlider" :header="defaults[locale].website.reviewsHeader" />

		<frequently-asked-questions
			v-if="page.frequentlyAskedQuestion"
			:header="page.frequentlyAskedQuestion.header"
			:items="page.frequentlyAskedQuestion.faqitems"
		/>

		<newsletter
			v-if="page.showNewsletter"
			:header="defaults[locale].newsletter.title"
			:content="defaults[locale].newsletter.content"
			:button="defaults[locale].newsletter.button"
		/>
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();
const localePath = useLocalePath();

await fetchPage();
await fetchDefaults();

const { data: roomsData } = await useWebsiteFetch('rooms', {
	query: { language: locale.value },
	key: `${locale.value}/rooms`,
});
</script>

<style lang="scss" scoped>
iframe {
	margin-top: 50px;
}

.rooms {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 70px;
	overflow: hidden;
	padding: 0 30px;
}

.room-item {
	min-width: 0;
	float: none;
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	padding: 0;

	.hidden-overflow {
		overflow-x: hidden;
	}

	.content {
		display: flex;
		flex-direction: column;
		height: 100%;

		.rooms-button {
			margin-top: auto;
		}
	}
}

.room-icons {
	display: flex;
	flex-flow: wrap row;
	justify-content: flex-start;
	align-items: center;
	gap: 10px;
	margin: 0 0 20px;

	.room-icon {
		margin: 0 20px 0 0;
	}

	span {
		font-size: 12px;
	}

	svg {
		color: var(--primary-color);
		margin-right: 5px;
	}
}

.room-img {
	width: 108%;
	max-width: none;
	transition: margin 0.8s ease-out;
}

.rooms a:hover .room-img {
	margin-left: -8%;
}

@media (max-width: 960px) {
	.rooms {
		grid-template-columns: 1fr;
	}
}
</style>
